/**
 * userSlice.ts
 * Copyright (c) 2023 James Ugbanu.
 * Licensed under the MIT License.
 */


import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import axios from "axios";
import {HOST} from "../api/constants";
import {User} from "../types/user";
import {auth} from "../utils/firebase";
import {signInWithCustomToken, getIdToken, signInWithEmailAndPassword, browserLocalPersistence} from "@firebase/auth";
import {FirebaseError} from "@firebase/app";

const authErrorCodeMessages = [{
    field: "auth",
    code: 'auth/invalid-credential',
    message: 'Email ou mot de passe incorrect'
}]


export const login = createAsyncThunk("user/login", async (credentials?: {
    email?: string,
    password?: string
}) => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password)
        const idToken = await userCredential.user.getIdToken()
        const currentUserResponse = await axios.get(HOST + "/auth/currentAdmin", {
            headers: {
                "Authorization": idToken
            }
        })

        return {
            user: {...currentUserResponse.data['data'], token: idToken},
            status: currentUserResponse.status
        }
    } catch (err) {
        console.log(err)
        if (err.code === authErrorCodeMessages[0].code)
            return {
                message: authErrorCodeMessages[0].message,
                code: 400
            }

        return {
            message: "Une erreur inconnue s'est produite",
            code: 500
        }
    }
})

export const logout = createAsyncThunk("user/logout", async () => {
    localStorage.removeItem("impactCarCurrentUser")
    await auth.signOut()
    window.location.reload()
})

const user = createSlice({
    name: "user",
    initialState: () => {
        return {
            isLoading: false,
            isLoggingOut: false,
            errorMsg: null,
            user: JSON.parse(localStorage.getItem("impactCarCurrentUser")) as User,
            invalidCredentials: false,
        }

    },
    reducers: {
        reset: (state) => {
            state.user = null
            localStorage.removeItem("impactCarCurrentUser")
        },
        updateUser(state, action: PayloadAction<User>) {
            state.user = {
                ...state.user,
                ...action.payload
            }
            localStorage.setItem("impactCarCurrentUser", JSON.stringify({
                ...state.user,
                ...action.payload
            }))
        },
        resetInvalidCredentials(state, action: PayloadAction<void>) {
            state.invalidCredentials = false
        }
    },
    extraReducers: builder => {
        builder.addCase(login.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(logout.pending, (state, action) => {
            state.isLoggingOut = true
        })

        builder.addCase(login.fulfilled, (state, action) => {
            state.isLoading = false

            if (action.payload['status'] !== 200) {
                state.invalidCredentials = true
                state.errorMsg = action.payload['message'] as string
                return
            }

            const user = action.payload['user']
            state.user = user
            localStorage.setItem("impactCarCurrentUser", JSON.stringify(user))
        })


        builder.addCase(logout.fulfilled, (state, action) => {
            state.isLoggingOut = false
            if (!action.payload['status'] || action.payload['status'] !== 200) {
                state.errorMsg = ["Unable to logout, please try again"]
                return
            }
            state.user = undefined
            localStorage.removeItem("impactCarCurrentUser")
        })

    }

})

export const {
    reset,
    updateUser,
    resetInvalidCredentials,
} = user.actions
export default user.reducer